import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, Signal } from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable, of, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { ElementActions, ElementTypes, GtmPurchaseFlow, GtmTripType } from '@fcom/common/interfaces';
import { Amount, LocationInfoMap } from '@fcom/dapi/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { ButtonPosition, ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { MediaQueryService } from '@fcom/common/services';
import { nonSeatAncillarySubTotalForJourneySelector } from '@fcom/common-booking/store/selectors/ancillary-sub-total.selector';
import { selectCartFlightTotalPrice } from '@fcom/booking/store/selectors';
import { sumAmounts } from '@fcom/common-booking/utils/amount.utils';

import { EligibilityWithCategory, FlightWithStreams, ItineraryModal, SeatChangeEvent } from '../../../interfaces';
import { SeatMapActions, seatSelections, ServicesActions } from '../../../../../store';
import { getSeatMapFlightInfoFromCart } from '../../../utils';
import { getBoundDetailsModalData } from '../../../../../utils/ancillaries.utils';
import { AncillaryLoaderStatus } from '../../../enums';
import { ShoppingCartService } from '../../../../../services/shopping-cart.service';

@Component({
  selector: 'fin-seat-pre-selection',
  templateUrl: './seat-pre-selection.component.html',
  styleUrls: ['./seat-pre-selection.component.scss'],
  standalone: false,
})
export class SeatPreSelectionComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<BookingAppState>>(Store);
  private gtmService = inject(GtmService);
  private shoppingCartService = inject(ShoppingCartService);
  private mediaQueryService = inject(MediaQueryService);

  readonly Category = Category;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ModalButtons = ModalButtons;
  readonly SeatMapStatus = SeatMapStatus;

  @Input() flights$: Observable<FlightWithStreams[]>;
  @Input() travelers$: Observable<FinnairPassengerItem[]>;
  @Input() tripType$: Observable<GtmTripType>;
  @Input() purchaseFlow$: Observable<GtmPurchaseFlow>;
  @Input() bounds$: Observable<FinnairBoundItem[]>;
  @Input() locations: LocationInfoMap = {};
  @Input()
  flightAncillaryAvailability$: Observable<Record<string, EligibilityWithCategory[]>>;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input()
  passengersWithPetcIncompatibleSeats$: Observable<Record<string, string[]>> = of({});
  @Input() combinedSeats: FinnairServiceItem | null;
  @Input() selectionLoading$: Observable<boolean>;
  @Input() totalPoints$: Observable<number>;

  @Output() preSelectClosed = new EventEmitter<boolean>();
  @Output() flightSelected = new EventEmitter<FinnairItineraryItemFlight>();
  @Output() seatChanged = new EventEmitter<SeatChangeEvent>();

  slideIndex$ = new BehaviorSubject(0);
  itineraryModalClosed$: EventEmitter<boolean> = new EventEmitter();
  hasSelections$: Observable<boolean>;
  totalPrice$: Observable<Amount>;
  subscriptions: Subscription = new Subscription();
  itineraryModal: ItineraryModal = {
    open: false,
    data: null,
  };
  translationKey$: Observable<{ key?: string; params?: Record<string, string | number> }>;
  passengers$: Observable<FinnairPassengerItem[]>;
  showFlightCard$: Observable<boolean> = of(true);
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject(AncillaryLoaderStatus.OFF);
  seatMapPreloadingEnabled$: Observable<boolean> = of(false);
  isMobile: Signal<boolean>;
  shoppingCartEnabled: boolean;

  ngOnInit(): void {
    this.seatMapPreloadingEnabled$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => cartOrOrder.otherInformation.useSeatmapPrefetch),
      finShare()
    );
    this.passengers$ = this.cartOrOrder$.pipe(map((cartOrOrder) => cartOrOrder.passengers));

    this.shoppingCartEnabled = this.shoppingCartService.isEnabled();
    this.isMobile = this.mediaQueryService.isBreakPoint('tablet_down');

    this.totalPrice$ = combineLatest([
      this.store$.select(selectCartFlightTotalPrice),
      this.store$.select(nonSeatAncillarySubTotalForJourneySelector(Category.SEAT)),
    ]).pipe(map(([totalPrice, subTotalPrice]) => sumAmounts(totalPrice, subTotalPrice)));

    this.hasSelections$ = this.store$.select(seatSelections).pipe(
      map((selections) => Object.values(selections).some((selection) => selection.some((s) => s.seatNumber))),
      finShare()
    );

    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$])
        .pipe(
          map(([slideIndex, flights]) => {
            return flights[slideIndex];
          })
        )
        .subscribe((currentFlight) => {
          this.flightSelected.emit(currentFlight);
        })
    );

    this.translationKey$ = combineLatest([this.slideIndex$, this.flights$, this.hasSelections$]).pipe(
      map(([slideIndex, flights, hasSelections]) => {
        const isLast = slideIndex === flights.length - 1;
        if (hasSelections) {
          return isLast ? { key: 'actions.next' } : { key: 'ancillaries.seats.continueToFlight' };
        } else {
          return isLast ? { key: 'ancillaries.seats.skipSeatSelection' } : { key: 'ancillaries.seats.skipFlight' };
        }
      })
    );
  }

  continue(): void {
    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$, this.hasSelections$])
        .pipe(take(1))
        .subscribe(([slideIndex, flights, hasSelections]) => {
          const isLast = slideIndex === flights.length - 1;

          if (hasSelections) {
            this.trackEvent('continue-selection');
          }

          if (!isLast) {
            this.slideIndex$.next(slideIndex + 1);
          } else {
            this.confirmSelection();
          }

          this.trackEvent('skip-selection-bottom', 'bottom');
        })
    );
  }

  confirmSelection(): void {
    this.trackEvent('confirm-selection');
    // setTimeout so that sidebar is closed properly before this component is destroyed
    setTimeout(() => {
      this.preSelectClosed.emit(true);
    });
  }

  skipSeatSelection(trackElementState: string): void {
    this.trackEvent(`skip-selection-${trackElementState}`, trackElementState);
    // Clear cart only if user has made seat selection to avoid unnecessary back-end calls
    this.subscriptions.add(
      this.hasSelections$.pipe(take(1)).subscribe((hasSelections) => {
        if (hasSelections) {
          this.store$.dispatch(SeatMapActions.clearSelections());
          this.store$.dispatch(ServicesActions.clearBundleSelectionsForCategory({ category: Category.SEAT }));
        }
        this.preSelectClosed.emit(hasSelections);
      })
    );
  }

  changeSeats(e: SeatChangeEvent): void {
    this.seatChanged.emit(e);
  }

  viewDetails(): void {
    this.itineraryModal = getBoundDetailsModalData(this.cartOrOrder$, this.slideIndex$);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private trackEvent(elementName: string, state?: string): void {
    this.subscriptions.add(
      this.cartOrOrder$.pipe(take(1)).subscribe((cart) => {
        this.gtmService.seatMapEvent(elementName, ElementTypes.BUTTON, state, ElementActions.CLICK, {
          flightInfo: getSeatMapFlightInfoFromCart(cart),
        });
      })
    );
  }

  /**
   * This is a performance improvement and stops child components being destroyed and recreated when the flights array changes.
   */
  flightsTrackBy(_index: number, flight: FlightWithStreams): string {
    return flight.id;
  }

  openShoppingCart(): void {
    this.shoppingCartService.setVisible(true);
  }

  protected readonly SvgLibraryIcon = SvgLibraryIcon;
}
